import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Warning,
  Refresh,
  Schedule,
  Timer,
  TrendingUp,
  TrendingDown,
  PlayArrow,
  InfoOutlined,
  History as HistoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  analyticsJobService,
  type AnalyticsJobInfo,
  type AnalyticsJobHealth,
  type AnalyticsJobExecution,
} from '../services/analyticsJobService';

const AnalyticsJobMonitoring: React.FC = () => {
  const [jobs, setJobs] = useState<AnalyticsJobInfo[]>([]);
  const [healthStatus, setHealthStatus] = useState<AnalyticsJobHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [triggeringJob, setTriggeringJob] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // History modal state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [jobHistory, setJobHistory] = useState<AnalyticsJobExecution[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    loadData();

    if (autoRefresh) {
      const interval = setInterval(loadData, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Try to load jobs first (required)
      const jobsList = await analyticsJobService.listJobs();
      setJobs(jobsList);
      
      // Try to load health status (optional - don't fail if this errors)
      try {
        const health = await analyticsJobService.getHealth();
        setHealthStatus(health);
      } catch (healthErr) {
        console.warn('Failed to load health status:', healthErr);
        // Set a default health status if health endpoint fails
        setHealthStatus({
          status: 'healthy',
          message: 'Health status unavailable',
          scheduler_running: true,
          recent_failures: 0,
          total_executions_24h: 0,
        });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
      setError(`Failed to load analytics job monitoring data: ${errorMessage}`);
      console.error('Error loading analytics jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (
    status: string
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return <CheckCircle color="success" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'critical':
        return <Error color="error" />;
      default:
        return <Warning />;
    }
  };

  const getJobStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return 'success';
      case 'RUNNING':
        return 'warning';
      case 'FAILED':
        return 'error';
      case 'IDLE':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getJobDisplayName = (jobId: string) => {
    const names: Record<string, string> = {
      'page-snapshot': 'Page Snapshot',
      'change-detection': 'Change Detection',
      'oci-momentum': 'OCI Momentum',
      'experiment-analysis': 'Experiment Analysis',
      'anomaly-detection': 'Anomaly Detection',
      'metrics-aggregation': 'Metrics Aggregation',
      'data-cleanup': 'Data Cleanup',
      'recommendation-refresh': 'Recommendation Refresh',
    };
    return names[jobId] || jobId;
  };

  const handleTriggerJob = async (jobId: string) => {
    setTriggeringJob(jobId);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await analyticsJobService.triggerJob(jobId);
      setSuccessMessage(`${getJobDisplayName(jobId)} triggered: ${result.message}`);
      setTimeout(() => setSuccessMessage(null), 5000);
      setTimeout(() => loadData(), 2000);
    } catch (err: any) {
      setError(`Failed to trigger ${getJobDisplayName(jobId)}: ${err.message || 'Unknown error'}`);
    } finally {
      setTriggeringJob(null);
    }
  };

  const handleViewHistory = async (jobId: string) => {
    setSelectedJobId(jobId);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    
    try {
      const history = await analyticsJobService.getJobHistory(jobId, 50);
      setJobHistory(history);
    } catch (err: any) {
      console.error('Failed to load job history:', err);
      setJobHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCloseHistory = () => {
    setHistoryDialogOpen(false);
    setSelectedJobId(null);
    setJobHistory([]);
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const getSuccessRate = (execution: AnalyticsJobExecution) => {
    if (execution.records_processed === 0) return '0%';
    return `${((execution.records_success / execution.records_processed) * 100).toFixed(1)}%`;
  };

  if (loading && jobs.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading analytics job monitoring data...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Analytics Jobs Monitoring
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Chip
            label={autoRefresh ? 'Auto-refresh: ON' : 'Auto-refresh: OFF'}
            color={autoRefresh ? 'primary' : 'default'}
            onClick={() => setAutoRefresh(!autoRefresh)}
          />
          <Tooltip title="Refresh Now">
            <IconButton onClick={loadData} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Page Description */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main' }}>
        <Box display="flex" gap={1.5}>
          <InfoOutlined color="info" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Analytics Service Batch Jobs
            </Typography>
            <Typography variant="body2" paragraph>
              Monitor and manage 8 Python batch jobs that process SEO data:
            </Typography>
            <Typography variant="body2" component="div">
              • <strong>Page Snapshot</strong> - Daily at 3:00 AM - <em>Input: ALL active pages from 'pages' table (is_active=true)</em> - Crawls HTML content via httpx GET requests, extracts metadata (title, description, headings, JSON-LD, canonical, robots), and stores in analytics_page_snapshots for change tracking<br />
              • <strong>Change Detection</strong> - Daily at 4:00 AM - <em>Input: Latest 2 snapshots per page from analytics_page_snapshots</em> - Compares consecutive snapshots field-by-field (title, description, H1, canonical, robots, content hash) and records changes in analytics_change_history<br />
              • <strong>OCI Momentum</strong> - Daily at 5:00 AM - <em>Input: 30-day search metrics from GSC, Bing, GA4 (multi-source daily metrics tables)</em> - Calculates OCI scores (0-100 competitiveness) and momentum trends (POSITIVE/NEUTRAL/DECLINING) from search metrics using weighted formula<br />
              • <strong>Experiment Analysis</strong> - Daily at 6:00 AM - <em>Input: Active experiments from experiments table + GSC/Bing/GA4 page metrics</em> - Runs t-tests on A/B experiments, calculates p-values, uplift percentages, and confidence intervals for test vs control groups<br />
              • <strong>Anomaly Detection</strong> - Daily at 7:00 AM - <em>Input: 90-day historical metrics from GSC, Bing, GA4 daily metrics tables</em> - Uses Z-score method (3σ threshold) to detect unusual spikes/drops in clicks, impressions, position, CTR over baseline<br />
              • <strong>Metrics Aggregation</strong> - Daily at 8:00 AM - <em>Input: All tenant metrics from GSC, Bing, GA4 daily metrics tables</em> - Computes daily analytics for all tenants: OCI scores, momentum indicators, SERP awareness metrics, and opportunity scores<br />
              • <strong>Data Cleanup</strong> - Daily at 10:00 AM - <em>Input: All analytics tables (GSC/Bing/GA4 daily metrics, analytics_aggregated, batch_job_executions)</em> - Removes old data beyond retention periods (raw metrics: 90 days, aggregated: 365 days, job executions: 90 days)<br />
              • <strong>Recommendation Refresh</strong> - Weekly on Sundays at 9:00 AM - <em>Input: Current recommendations table + latest metrics/analysis results from all sources</em> - Marks expired recommendations and generates new actionable suggestions using the recommendation engine
            </Typography>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Manual Trigger Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Manual Job Triggers
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {jobs.map((job) => (
            <Button
              key={job.job_id}
              variant="outlined"
              size="small"
              startIcon={triggeringJob === job.job_id ? <CircularProgress size={16} /> : <PlayArrow />}
              onClick={() => handleTriggerJob(job.job_id)}
              disabled={triggeringJob === job.job_id}
            >
              {getJobDisplayName(job.job_id)}
            </Button>
          ))}
        </Box>
      </Paper>

      {/* Health Status Overview */}
      {healthStatus && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {getHealthIcon(healthStatus.status)}
                  <Typography variant="h6">System Health</Typography>
                </Box>
                <Chip label={healthStatus.status} color={getHealthColor(healthStatus.status)} size="small" />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {healthStatus.message}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Scheduler Status
                </Typography>
                <Chip
                  label={healthStatus.scheduler_running ? 'Running' : 'Stopped'}
                  color={healthStatus.scheduler_running ? 'success' : 'error'}
                  size="small"
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: healthStatus.recent_failures > 0 ? 'error.50' : 'inherit' }}>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Recent Failures
                </Typography>
                <Typography variant="h4" color={healthStatus.recent_failures > 0 ? 'error' : 'inherit'}>
                  {healthStatus.recent_failures}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Executions (24h)
                </Typography>
                <Typography variant="h4">{healthStatus.total_executions_24h}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Job Details Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job Name</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Next Run Time</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow
                  key={job.job_id}
                  sx={{
                    bgcolor:
                      job.status?.toUpperCase() === 'FAILED'
                        ? 'error.50'
                        : job.status?.toUpperCase() === 'RUNNING'
                        ? 'warning.50'
                        : 'inherit',
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {getJobDisplayName(job.job_id)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {job.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Schedule fontSize="small" color="action" />
                      <Typography variant="body2">{job.trigger || 'N/A'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={job.status || 'UNKNOWN'} color={getJobStatusColor(job.status)} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize="0.875rem">
                      {formatTime(job.next_run_time)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      <Tooltip title="Trigger Job Now">
                        <span>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleTriggerJob(job.job_id)}
                            disabled={triggeringJob === job.job_id}
                          >
                            {triggeringJob === job.job_id ? <CircularProgress size={20} /> : <PlayArrow />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="View Execution History">
                        <IconButton
                          color="default"
                          size="small"
                          onClick={() => handleViewHistory(job.job_id)}
                        >
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {jobs.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 3 }}>
          No analytics jobs found. Make sure the Analytics Service is running and jobs are registered.
        </Alert>
      )}

      {/* Execution History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={handleCloseHistory}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Execution History: {selectedJobId ? getJobDisplayName(selectedJobId) : ''}
            </Typography>
            <IconButton onClick={handleCloseHistory} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : jobHistory.length === 0 ? (
            <Alert severity="info">
              No execution history found for this job.
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Started At</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">Processed</TableCell>
                    <TableCell align="right">Success</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Success Rate</TableCell>
                    <TableCell>Triggered By</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobHistory.map((execution) => (
                    <TableRow
                      key={execution.id}
                      sx={{
                        bgcolor:
                          execution.status === 'FAILED'
                            ? 'error.50'
                            : execution.status === 'RUNNING'
                            ? 'warning.50'
                            : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontSize="0.75rem">
                          {formatTime(execution.started_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={execution.status}
                          color={getJobStatusColor(execution.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Timer fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatDuration(execution.duration_seconds)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{execution.records_processed}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          {execution.records_success}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={execution.records_failed > 0 ? 'error.main' : 'inherit'}
                        >
                          {execution.records_failed}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          {Number(getSuccessRate(execution).replace('%', '')) >= 95 ? (
                            <TrendingUp fontSize="small" color="success" />
                          ) : (
                            <TrendingDown fontSize="small" color="error" />
                          )}
                          <Typography variant="body2">{getSuccessRate(execution)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={execution.triggered_by} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {execution.error_message ? (
                          <Tooltip title={execution.error_message}>
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                              {execution.error_message}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistory}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AnalyticsJobMonitoring;
